
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Table,
    Spinner,
    Button,
    ButtonGroup
} from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import { FaArrowRight, FaDownload, FaPrint } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import './ViewExam.css';

const baseURL = apiURL1;

function ComputeResultTab({ classId, type, typeId, year, examDetails, uniClassName }) {
    const [activeTab, setActiveTab] = useState('byClass');
    const navigate = useNavigate();

    return (
        <Container>
            <Row>
                <Col>
                    <div className="d-flex justify-content-center mb-5">
                        <Button
                            color={activeTab === "byClass" ? "info" : "secondary"}
                            onClick={() => setActiveTab("byClass")}
                            style={{ marginRight: '10px' }}
                        >
                            By Class
                        </Button>
                        <Button
                            color={activeTab === "bySubject" ? "info" : "secondary"}
                            onClick={() => setActiveTab("bySubject")}
                        >
                            By Subject
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    {activeTab === 'byClass' && (
                        <ByClassResults classId={classId} type={type} typeId={typeId} year={year} uniClassName={uniClassName} />
                    )}
                    {activeTab === 'bySubject' && (
                        <BySubjectList examDetails={examDetails} navigate={navigate} classId={classId} type={type} typeId={typeId} year={year} uniClassName={uniClassName} />
                    )}
                </Col>
            </Row>
        </Container>
    );
}

function ByClassResults({ classId, type, typeId, year, uniClassName }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { id } = useParams();
    const [overallStatus, setOverallStatus] = useState('');
    const [schoolInfo, setSchoolInfo] = useState({});
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchComputeResults();
        fetchSchoolInfo();
    }, [classId, typeId, year]);

    const fetchSchoolInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            const data = response.data;
            if (data && data.length > 0) {
                setSchoolInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching school info:', error);
        }
    };

    const fetchComputeResults = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${baseURL}/superadmin/results/class/${classId}/type/${typeId}/year/${year}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            }
            );
            setResults(response.data);
            setOverallStatus(response.data.overallStatus || 'pending');
        } catch (error) {
            console.error('Error fetching computed results:', error);
            toast.error('Error fetching computed results');
        } finally {
            setLoading(false);
        }
    };

    const generatePdfReport = async () => {
        try {
            // Fetch fresh school info
            const schoolResponse = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const schoolData = schoolResponse.data[0] || {};

            const pdf = new jsPDF();
            const pageWidth = pdf.internal.pageSize.getWidth();

            // Add School Logo
            const img = new Image();
            img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";


            // School Information
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.text(schoolData.schoolName || 'Ummu Dhardha Ladies Islamic College', 50, 22);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.text(schoolData.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.', 50, 28);
            pdf.text(`Tel: ${schoolData.schoolPhoneNumber || '+94 67 205 3211'} | Email: ${schoolData.schoolEmail || 'info@ummudhardha.lk'}`, 50, 34);

            // Report Title
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Class Results - ${type} ${year}`, pageWidth / 2, 45, { align: 'center' });
            pdf.setFontSize(12);
            pdf.text(`Class: ${uniClassName}`, pageWidth / 2, 52, { align: 'center' });

            let y = 60;
            const rankColors = ['#2ecc71', '#3498db', '#9b59b6'];

            // Top Three Ranks Section
            if (results.topThree && results.topThree.length > 0) {
                pdf.setFontSize(12);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Top Three Performers', 15, y);
                y += 8;

                results.topThree.forEach((student, index) => {
                    pdf.setFillColor(rankColors[index]);
                    pdf.roundedRect(15, y, pageWidth - 30, 20, 3, 3, 'F');

                    pdf.setFontSize(12);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(255, 255, 255);
                    pdf.text(`Rank ${student.rank}: ${student.fullName}`, 20, y + 8);

                    pdf.setFontSize(10);
                    pdf.text(`Reg: ${student.registrationNumber} | Marks: ${student.obtainedMarks}/${student.totalMarks} | Grade: ${student.grade}`,
                        20, y + 15);

                    y += 25;
                    pdf.setTextColor(0, 0, 0);
                });
                y += 10;
            }

            // All Students Table
            if (results.data && results.data.length > 0) {
                pdf.setFontSize(12);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Detailed Results', 15, y);
                y += 8;

                const headers = [['Rank', 'Registration No.', 'Student Name', 'Marks Obtained', 'Total Marks', 'Grade']];
                const tableData = results.data.map(student => [
                    student.rank,
                    student.registrationNumber,
                    student.fullName,
                    student.obtainedMarks,
                    student.totalMarks,
                    student.grade
                ]);

                pdf.autoTable({
                    startY: y,
                    head: headers,
                    body: tableData,
                    theme: 'grid',
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontSize: 10,
                        fontStyle: 'bold'
                    },
                    bodyStyles: { fontSize: 10 },
                    alternateRowStyles: { fillColor: [245, 245, 245] },
                    margin: { left: 15, right: 15 },
                    styles: { cellPadding: 3 },
                    columnStyles: {
                        0: { cellWidth: 15 },
                        1: { cellWidth: 30 },
                        2: { cellWidth: 50 },
                        3: { cellWidth: 25 },
                        4: { cellWidth: 25 },
                        5: { cellWidth: 20 }
                    }
                });
            }

            // Footer
            const totalPages = pdf.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(8);
                pdf.text(`Generated on: ${new Date().toLocaleDateString()} | ${schoolData.schoolName || 'Ummu Dhardha Ladies Islamic College'}`,
                    pageWidth - 40, pdf.internal.pageSize.getHeight() - 10);
            }

            pdf.save(`results-${uniClassName}-${type}-${year}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error generating PDF report');
        }
    };

    const handlePrint = async () => {
        try {
            const schoolResponse = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const schoolData = schoolResponse.data[0] || {};

            const tableStyle = `
        border-collapse: collapse;
        width: 100%;
        margin-top: 20px;
      `;

            const thStyle = `
        background-color: #2980b9;
        color: white;
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      `;

            const tdStyle = `
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      `;

            const printContent = `
        <html>
          <head>
            <title>Class Results - ${uniClassName}</title>
            <style>
              @page { margin: 20px; }
              body { font-family: Arial; margin: 20px; }
              .header { text-align: center; margin-bottom: 20px; }
              .logo { width: 100px; height: 100px; }
              .school-name { font-size: 24px; font-weight: bold; margin: 10px 0; }
              .report-title { font-size: 20px; margin: 15px 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 15px; }
              th { background-color: #2980b9; color: white; padding: 8px; text-align: left; }
              td { padding: 8px; border-bottom: 1px solid #ddd; }
              .top-rank { background-color: #2ecc71; color: white; margin: 10px 0; padding: 10px; border-radius: 5px; }
              .footer { margin-top: 20px; font-size: 12px; text-align: center; }
            </style>
          </head>
          <body>
            <div class="header">
              <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" 
                   class="logo" alt="School Logo">
              <div class="school-name">${schoolData.schoolName || 'Ummu Dhardha Ladies Islamic College'}</div>
              <div class="school-info">
                ${schoolData.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.'}<br>
                Tel: ${schoolData.schoolPhoneNumber || '+94 67 205 3211'} | 
                Email: ${schoolData.schoolEmail || 'info@ummudhardha.lk'}
              </div>
            </div>
            
            <div class="report-title">Class Results - ${type} ${year}</div>
            <div class="class-name">Class: ${uniClassName}</div>

            ${results.topThree && results.topThree.length > 0 ? `
              <h3>Top Three Performers</h3>
              ${results.topThree.map((student, index) => `
                <div class="top-rank" style="background-color: ${['#2ecc71', '#3498db', '#9b59b6'][index]}">
                  <h4>Rank ${student.rank}: ${student.fullName}</h4>
                  <p>Registration: ${student.registrationNumber}</p>
                  <p>Marks: ${student.obtainedMarks}/${student.totalMarks} | Grade: ${student.grade}</p>
                </div>
              `).join('')}
            ` : ''}

            ${results.data && results.data.length > 0 ? `
              <h3>All Student Results</h3>
              <table style="${tableStyle}">
                <thead>
                  <tr>
                    <th style="${thStyle}">Rank</th>
                    <th style="${thStyle}">Registration No.</th>
                    <th style="${thStyle}">Student Name</th>
                    <th style="${thStyle}">Marks Obtained</th>
                    <th style="${thStyle}">Total Marks</th>
                    <th style="${thStyle}">Grade</th>
                  </tr>
                  </thead>
                  <tbody>
                    ${results.data.map(student => `
                      <tr>
                        <td style="${tdStyle}">${student.rank}</td>
                        <td style="${tdStyle}">${student.registrationNumber}</td>
                        <td style="${tdStyle}">${student.fullName}</td>
                        <td style="${tdStyle}">${student.obtainedMarks}</td>
                        <td style="${tdStyle}">${student.totalMarks}</td>
                        <td style="${tdStyle}">${student.grade}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
            ` : ''}

            <div class="footer">
              Generated on: ${new Date().toLocaleDateString()} | ${schoolData.schoolName || 'Ummu Dhardha Ladies Islamic College'}
            </div>
          </body>
        </html>
      `;

            const printWindow = window.open('', '_blank');
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.print();
        } catch (error) {
            console.error('Error generating print content:', error);
            toast.error('Error generating print view');
        }
    };

    const handleStatusUpdate = async () => {
        setLoading(true);
        const newStatus = overallStatus === 'published' ? 'pending' : 'published';
        try {
            await axios.put(`${baseURL}/superadmin/results/live/${id}`, { status: newStatus }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            toast.success(`Results status changed to ${newStatus === 'published' ? 'Published' : 'Draft'}!`);
            fetchComputeResults(); // Refresh results
        } catch (error) {
            console.error('Error updating results status:', error);
            toast.error('Error updating results status');
        } finally {
            setLoading(false);
        }
    };

    const handleStudentClick = (studentId) => {
        navigate(`/admin/view-student-result/${studentId}/${typeId}/${year}`, {
            state: {
                className: uniClassName,
                typeName: type,
                year: year,
            }
        });
    };

    if (loading) {
        return (
            <Container>
                <Row>
                    <Col>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Spinner color="primary" />
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (!results) {
        return <p>No result found yet.</p>;
    }

    const rankColors = ['#2ecc71', '#3498db', '#9b59b6'];

    return (
        <Container>
            {(results.data.length > 0 ? <Row className="mb-3" style={{ justifyContent: 'space-between' }}>
                <Col>
                    <h4>Class: {uniClassName}</h4>
                    <p>Results are {overallStatus === 'published' ? 'Published' : 'Draft'}</p>
                    <Button color={overallStatus === 'published' ? 'warning' : 'success'} onClick={handleStatusUpdate} disabled={loading}>
                        {overallStatus === 'published' ? 'Make Draft' : 'Publish'}
                    </Button>
                </Col>
            </Row> : <></>)}


            <Row className="mb-3" style={{ justifyContent: 'space-between' }}>

                <Col>
                    <h4>Top Three Ranks</h4>
                </Col>
                <Col style={{ textAlign: 'right' }}>
                    <ButtonGroup>
                        <Button color="success" size="sm" onClick={generatePdfReport} style={{ marginRight: '10px' }}>
                            <FaDownload /> Download PDF
                        </Button>
                        <Button color="info" size="sm" onClick={handlePrint}>
                            <FaPrint /> Print
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>

            <div className="print-only">
                <h4>Class Results - {type} {year}</h4>
                {results.topThree && results.topThree.length > 0 && (
                    <>
                        <h5>Top Three Ranks</h5>
                        {results.topThree.map((student, index) => (
                            <div key={student.studentId}>
                                <p>Rank {student.rank}: {student.fullName} (Reg: {student.registrationNumber}, Obtained: {student.obtainedMarks}, Total: {student.totalMarks}, Grade: {student.grade})</p>
                            </div>
                        ))}
                    </>
                )}
            </div>

            {results.topThree && results.topThree.length > 0 ? (
                <Row className="">
                    {results.topThree.map((student, index) => (
                        <Col md="4" key={student.studentId} className="mb-3">
                            <Card style={{
                                backgroundColor: rankColors[index],
                                color: 'white',
                                textAlign: 'center',
                                borderRadius: '15px',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                            }}>
                                <CardBody>
                                    <h5>Rank: {student.rank}</h5>
                                    <p>Name: {student.fullName}</p>
                                    <p>Registration Number: {student.registrationNumber}</p>
                                    <p>Obtained Marks: {student.obtainedMarks}</p>
                                    <p>Total Marks: {student.totalMarks}</p>
                                    <p>Grade: {student.grade}</p>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : (
                <p>No top three ranks available.</p>
            )}

            {results.data && results.data.length > 0 ? (
                <>
                    <h4>All Student Results</h4>
                    <div className="table-responsive">
                        <Table className="table">
                            <thead>
                                <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                    <th>Rank</th>
                                    <th>Registration Number</th>
                                    <th>Name</th>
                                    <th>Obtained Marks</th>
                                    <th>Total Marks</th>
                                    <th>Grade</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.data.map((student) => (
                                    <tr key={student.studentId}>
                                        <td>{student.rank}</td>
                                        <td>{student.registrationNumber}</td>
                                        <td>{student.fullName}</td>
                                        <td>{student.obtainedMarks}</td>
                                        <td>{student.totalMarks}</td>
                                        <td>{student.grade}</td>
                                        <td style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleStudentClick(student.studentId)}>
                                            <FaArrowRight />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </>
            ) : (
                <p>No student results available.</p>
            )}
        </Container>
    );
}


function BySubjectList({ examDetails, navigate, classId, type, typeId, year, uniClassName }) {
    // console.log('Exam details is ', examDetails)
    const handleSubjectClick = (subjectId, subjectName) => {
        navigate(`/admin/view-subject-result/${classId}/${subjectId}/${typeId}/${year}`, {
            state: {
                className: uniClassName ? uniClassName : 'N/A',
                subjectName: subjectName,
                typeName: type,
                typeId: typeId,
                year: year
            }
        });
    };

    return (
        <Container>
            {examDetails && Array.isArray(examDetails) && examDetails.length > 0 ? (
                <div className="table-responsive">
                    <Table className="table">
                        <thead>
                            <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                <th>Subject Code</th>
                                <th>Subject</th>
                                <th>Staff</th>
                                <th>Date</th>
                                <th>Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {examDetails.map((detail, index) => (
                                <tr key={detail._id} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                                    <td>{detail.subject.subjectCode}</td>
                                    <td>{detail.subject.name}</td>
                                    <td>{detail.teacher ? detail.teacher.name : 'N/A'}</td>
                                    <td>{dayjs(detail.examDate).format('DD-MM-YYYY')}</td>
                                    <td style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSubjectClick(detail.subject._id, detail.subject.name)}>
                                        <FaArrowRight />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <p>No subjects available.</p>
            )}
        </Container>
    );
}

export default ComputeResultTab;
